import { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, FileText, Plus } from 'lucide-react';
import React, { forwardRef, useImperativeHandle } from 'react';
import { apiFetch, getUser } from '../api/client';
import { AddPointModal } from './AddPointModal';

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface AxisEntry {
  axis_num: number;
  in_alarm: boolean;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  velocityThresholds: Thresholds;
  accelerationThresholds: Thresholds;
  envelopeThresholds: Thresholds;
  temperatureThresholds: Thresholds;
  status: 'low' | 'medium' | 'high';
  sensorSerial?: string;
  batteryVoltage?: number;
  isConnected?: boolean;
  lastDataTimestamp?: Date;
  axes?: AxisEntry[];
}

interface Machine {
  id: string;
  name: string;
  health: 'healthy' | 'warning' | 'critical';
  hasNewReport?: boolean;
  imageUrl?: string;
  points: Point[];
}

const calculateMachineHealth = (points: Point[]): 'healthy' | 'warning' | 'critical' => {
  const hasHigh = points.some(p => p.status === 'high');
  const hasMedium = points.some(p => p.status === 'medium');
  if (hasHigh) return 'critical';
  if (hasMedium) return 'warning';
  return 'healthy';
};

const findCulpritPointAndVariable = (points: Point[]): { point: Point; metric: string; variable: 'V' | 'H' | 'A'; value: number } | null => {
  let culprit = null;
  let highestSeverity = 0;
  for (const point of points) {
    const metrics: Array<{ name: string; data: PointData; thresholds: Thresholds }> = [
      { name: 'Velocity', data: point.velocity, thresholds: point.velocityThresholds },
      { name: 'Acceleration', data: point.acceleration, thresholds: point.accelerationThresholds },
      { name: 'Envelope', data: point.envelope, thresholds: point.envelopeThresholds },
      { name: 'Temperature', data: point.temperature, thresholds: point.temperatureThresholds },
    ];
    for (const metric of metrics) {
      for (const variable of ['V', 'H', 'A'] as const) {
        const value = metric.data[variable];
        let severity = 0;
        if (value >= metric.thresholds.red) severity = 2;
        else if (value >= metric.thresholds.yellow) severity = 1;
        if (severity > highestSeverity) {
          highestSeverity = severity;
          culprit = { point, metric: metric.name, variable, value };
        }
      }
    }
  }
  return culprit;
};

// ---------------------------------------------------------------------------
// Tree data model — 4 levels: Plant → Company → SubArea → Machine
//   Berry:    Plant(Berry) → Company(M4/M5/M6) → SubArea(Filtration/…) → Machine
//   Arcadium: Plant(Arcadium) → Company(LIOH1/LIOH2, isAreaLevel=true) → Machine
// ---------------------------------------------------------------------------
interface SubArea {
  id: string;
  name: string;
  machines: Machine[];
}

interface Company {
  id: string;
  name: string;
  subAreas: SubArea[];   // non-empty for Berry
  machines: Machine[];   // non-empty for Arcadium (direct machines)
  isAreaLevel: boolean;  // true when this node is actually an area_name (Arcadium)
}

interface Plant {
  id: string;
  name: string;
  companies: Company[];
}

const getAllMachinesFromPlant = (plant: Plant): Machine[] =>
  plant.companies.flatMap(c => [...c.machines, ...c.subAreas.flatMap(a => a.machines)]);

// ---------------------------------------------------------------------------
// Filter type — exported for use in App / DashboardSummary
// ---------------------------------------------------------------------------
export interface HierarchyFilter {
  dbName: string;
  companyName?: string;  // Berry: M4/M5/M6 (from company_name); undefined for Arcadium
  areaName?: string;     // Berry subarea (Filtration) or Arcadium area (LIOH1/LIOH2)
  label: string;
}

// ---------------------------------------------------------------------------
// Component interfaces
// ---------------------------------------------------------------------------
interface TreeViewProps {
  onMachineSelect: (machineId: string, machineName: string, points: Point[], imageUrl?: string) => void;
  onPointSelect?: (machineId: string, machineName: string, points: Point[], pointId: string, imageUrl?: string, axisNum?: number) => void;
  onFilterSelect?: (filter: HierarchyFilter | null) => void;
  activeFilter?: HierarchyFilter | null;
}

export interface TreeViewRef {
  updateMachineImage: (machineId: string, imageUrl: string) => void;
  getAllMachines: () => Array<Machine & { plantName: string; areaName: string }>;
}

// ---------------------------------------------------------------------------
// API type + buildTree
// ---------------------------------------------------------------------------
interface ApiMachine {
  id: string;
  name: string;
  db_id: number;
  db_name: string;
  company_name: string;
  area_name: string;
  area_api_id: number;
  active_alarms: number;
}

function buildTree(apiMachines: ApiMachine[]): Plant[] {
  const plantMap = new Map<string, Plant>();

  for (const m of apiMachines) {
    const plantId = `plant-${m.db_id}`;
    if (!plantMap.has(plantId)) {
      plantMap.set(plantId, { id: plantId, name: m.db_name, companies: [] });
    }
    const plant = plantMap.get(plantId)!;
    const alarms = m.active_alarms || 0;
    const health: 'healthy' | 'warning' | 'critical' =
      alarms >= 5 ? 'critical' : alarms > 0 ? 'warning' : 'healthy';
    const machine: Machine = { id: m.id, name: m.name, health, points: [] };

    const hasDistinctCompany = m.company_name && m.company_name !== m.db_name;

    if (hasDistinctCompany) {
      // Berry-style: Plant → Company(M4/M5/M6) → SubArea(Filtration/…) → Machine
      const companyId = `company-${m.db_id}-${m.company_name}`;
      let company = plant.companies.find(c => c.id === companyId);
      if (!company) {
        company = { id: companyId, name: m.company_name, subAreas: [], machines: [], isAreaLevel: false };
        plant.companies.push(company);
      }
      const subAreaId = `subarea-${m.db_id}-${m.area_api_id}`;
      let subArea = company.subAreas.find(a => a.id === subAreaId);
      if (!subArea) {
        subArea = { id: subAreaId, name: m.area_name, machines: [] };
        company.subAreas.push(subArea);
      }
      subArea.machines.push(machine);
    } else {
      // Arcadium-style: Plant → Company(=LIOH1/LIOH2 area_name, isAreaLevel=true) → Machine
      const companyId = `company-${m.db_id}-${m.area_api_id}`;
      let company = plant.companies.find(c => c.id === companyId);
      if (!company) {
        company = { id: companyId, name: m.area_name, subAreas: [], machines: [], isAreaLevel: true };
        plant.companies.push(company);
      }
      company.machines.push(machine);
    }
  }
  return Array.from(plantMap.values());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const TreeView = forwardRef<TreeViewRef, TreeViewProps>(({ onMachineSelect, onPointSelect, onFilterSelect, activeFilter }, ref) => {
  const isAdmin = getUser()?.role === 'admin';
  const [expandedPlants, setExpandedPlants]     = useState<Set<string>>(new Set());
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [expandedSubAreas, setExpandedSubAreas] = useState<Set<string>>(new Set());
  const [expandedMachines, setExpandedMachines] = useState<Set<string>>(new Set());
  const [expandedPoints, setExpandedPoints]     = useState<Set<string>>(new Set());
  const [hoveredMachine, setHoveredMachine]     = useState<string | null>(null);
  const [treeData, setTreeData]   = useState<Plant[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError]     = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number;
    type: 'plant' | 'company' | 'subarea' | 'machine' | 'point';
    id: string; parentId?: string; grandParentId?: string;
  } | null>(null);
  const [editingItem, setEditingItem] = useState<{
    type: 'plant' | 'company' | 'subarea' | 'machine' | 'point';
    id: string; name: string;
    // extra context needed to build the correct API call
    dbId?: number;
    parentName?: string;   // company_name for subarea; machine_id for point
    isAreaLevel?: boolean; // true when company node represents an area
  } | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    type: 'plant' | 'company' | 'subarea' | 'machine' | 'point';
    id: string; name: string; parentId?: string;
  } | null>(null);
  const [addPointModal, setAddPointModal] = useState<{
    machineId: string; machineName: string; points: Point[]; imageUrl?: string;
  } | null>(null);
  const [selectedDbId, setSelectedDbId] = useState<string | null>(null);

  // Keep dropdown in sync when the filter is cleared externally (e.g. via dashboard "clear" button)
  useEffect(() => {
    if (!activeFilter) {
      setSelectedDbId(null);
    }
  }, [activeFilter]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTreeLoading(true);
    apiFetch<ApiMachine[]>('/machines')
      .then(data => {
        const tree = buildTree(data);
        setTreeData(tree);
        setTreeError(null);
        // Auto-expand all top-level plants on first load
        setExpandedPlants(new Set(tree.map(p => p.id)));
      })
      .catch(err => setTreeError(err.message || 'Failed to load machines'))
      .finally(() => setTreeLoading(false));
  }, []);

  const togglePlant = (id: string) => {
    const s = new Set(expandedPlants);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedPlants(s);
  };

  const toggleCompany = (id: string) => {
    const s = new Set(expandedCompanies);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedCompanies(s);
  };

  const toggleSubArea = (id: string) => {
    const s = new Set(expandedSubAreas);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedSubAreas(s);
  };

  const togglePoint = (key: string) => {
    const s = new Set(expandedPoints);
    s.has(key) ? s.delete(key) : s.add(key);
    setExpandedPoints(s);
  };

  const axisLabel = (n: number) => n === 1 ? 'H' : n === 2 ? 'V' : 'A';

  const toggleMachine = async (machineId: string) => {
    const s = new Set(expandedMachines);
    if (s.has(machineId)) {
      s.delete(machineId);
    } else {
      s.add(machineId);
      const machine = treeData.flatMap(getAllMachinesFromPlant).find(m => m.id === machineId);
      if (machine && machine.points.length === 0) {
        try {
          const detail = await apiFetch<{ axes: Array<{ point_name: string; axis_num: number; in_alarm: number }> }>(`/machines/${machineId}`);
          const pointMap = new Map<string, { inAlarm: number; axes: AxisEntry[] }>();
          for (const ax of detail.axes || []) {
            const entry = pointMap.get(ax.point_name) ?? { inAlarm: 0, axes: [] };
            entry.inAlarm = Math.max(entry.inAlarm, ax.in_alarm || 0);
            entry.axes.push({ axis_num: ax.axis_num, in_alarm: !!(ax.in_alarm) });
            pointMap.set(ax.point_name, entry);
          }
          const points: Point[] = Array.from(pointMap.entries()).map(([name, data]) => ({
            id: name, name,
            velocity: { V: 0, H: 0, A: 0 }, acceleration: { V: 0, H: 0, A: 0 },
            envelope: { V: 0, H: 0, A: 0 }, temperature: { V: 0, H: 0, A: 0 },
            velocityThresholds: { yellow: 0, red: 0 }, accelerationThresholds: { yellow: 0, red: 0 },
            envelopeThresholds: { yellow: 0, red: 0 }, temperatureThresholds: { yellow: 0, red: 0 },
            status: data.inAlarm ? 'high' : 'low',
            axes: data.axes.sort((a, b) => a.axis_num - b.axis_num),
          }));
          const patchMachine = (m: Machine) => m.id === machineId ? { ...m, points } : m;
          setTreeData(prev => prev.map(plant => ({
            ...plant,
            companies: plant.companies.map(company => ({
              ...company,
              machines: company.machines.map(patchMachine),
              subAreas: company.subAreas.map(area => ({
                ...area,
                machines: area.machines.map(patchMachine),
              })),
            })),
          })));
        } catch (err) { console.error('TreeView: machine detail fetch failed for', machineId, err); }
      }
    }
    setExpandedMachines(s);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getHealthTextColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-700';
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    type: 'plant' | 'company' | 'subarea' | 'machine' | 'point',
    id: string, parentId?: string, grandParentId?: string
  ) => {
    e.preventDefault(); e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type, id, parentId, grandParentId });
  };

  const findName = (id: string): string => {
    for (const plant of treeData) {
      if (plant.id === id) return plant.name;
      for (const company of plant.companies) {
        if (company.id === id) return company.name;
        for (const area of company.subAreas) {
          if (area.id === id) return area.name;
          for (const machine of area.machines) {
            if (machine.id === id) return machine.name;
            for (const point of machine.points) { if (point.id === id) return point.name; }
          }
        }
        for (const machine of company.machines) {
          if (machine.id === id) return machine.name;
          for (const point of machine.points) { if (point.id === id) return point.name; }
        }
      }
    }
    return '';
  };

  // ---------------------------------------------------------------------------
  // Tree refresh — re-fetches the full machine list from the API
  // ---------------------------------------------------------------------------
  const refreshTree = () => {
    apiFetch<ApiMachine[]>('/machines')
      .then(data => {
        setTreeData(buildTree(data));
        setMutationError(null);
      })
      .catch(() => {/* silently ignore refresh errors */});
  };

  // ---------------------------------------------------------------------------
  // Context helpers — extract db_id / parent info from the tree
  // ---------------------------------------------------------------------------
  const findPlantForId = (id: string): Plant | null => {
    for (const plant of treeData) {
      if (plant.id === id) return plant;
      for (const co of plant.companies) {
        if (co.id === id) return plant;
        for (const area of co.subAreas) {
          if (area.id === id) return plant;
          for (const m of area.machines) { if (m.id === id) return plant; }
        }
        for (const m of co.machines) { if (m.id === id) return plant; }
      }
    }
    return null;
  };

  const dbIdFromPlantId = (plantId: string) => parseInt(plantId.replace('plant-', '')) || 0;

  const handleRename = () => {
    if (!contextMenu) return;
    const { type, id, parentId, grandParentId } = contextMenu;
    const name = findName(id);

    let dbId: number | undefined;
    let parentName: string | undefined;
    let isAreaLevel: boolean | undefined;

    if (type === 'company') {
      const plant = findPlantForId(parentId || '');
      dbId = plant ? dbIdFromPlantId(plant.id) : undefined;
      isAreaLevel = treeData.flatMap(p => p.companies).find(c => c.id === id)?.isAreaLevel;
    } else if (type === 'subarea') {
      const plant = findPlantForId(grandParentId || '');
      dbId = plant ? dbIdFromPlantId(plant.id) : undefined;
      // parentId is company.id — get its name as parentName
      const co = treeData.flatMap(p => p.companies).find(c => c.id === (parentId || ''));
      parentName = co?.name;
    } else if (type === 'machine') {
      // No extra context needed for machine rename
    } else if (type === 'point') {
      parentName = parentId; // parentId IS the machine's DB id
    }

    setEditingItem({ type, id, name, dbId, parentName, isAreaLevel });
    setContextMenu(null);
  };

  const handleRenameSubmit = async (newName: string) => {
    if (!editingItem || !newName.trim()) { setEditingItem(null); return; }
    const trimmed = newName.trim();
    const { type, id, name: oldName, dbId, parentName, isAreaLevel } = editingItem;

    // Optimistically update local state first
    const patchMachine = (m: Machine) =>
      type === 'machine' && m.id === id ? { ...m, name: trimmed } :
      { ...m, points: m.points.map(p => type === 'point' && p.id === oldName ? { ...p, id: trimmed, name: trimmed } : p) };
    setTreeData(prev => prev.map(plant => {
      if (type === 'plant' && plant.id === id) return { ...plant, name: trimmed };
      return {
        ...plant,
        companies: plant.companies.map(company => {
          if (type === 'company' && company.id === id) return { ...company, name: trimmed };
          return {
            ...company,
            machines: company.machines.map(patchMachine),
            subAreas: company.subAreas.map(area => {
              if (type === 'subarea' && area.id === id) return { ...area, name: trimmed };
              return { ...area, machines: area.machines.map(patchMachine) };
            }),
          };
        }),
      };
    }));
    setEditingItem(null);

    // Persist to backend
    try {
      if (type === 'machine') {
        await apiFetch(`/machines/${id}`, { method: 'PATCH', body: JSON.stringify({ name: trimmed }) });
      } else if (type === 'point' && parentName) {
        await apiFetch(`/machines/${parentName}/points/${encodeURIComponent(oldName)}`,
          { method: 'PATCH', body: JSON.stringify({ new_name: trimmed }) });
      } else if (type === 'company' && dbId !== undefined) {
        const field = isAreaLevel ? 'area_name' : 'company_name';
        await apiFetch('/machines/rename-group',
          { method: 'PATCH', body: JSON.stringify({ field, db_id: dbId, old_value: oldName, new_value: trimmed }) });
      } else if (type === 'subarea' && dbId !== undefined) {
        await apiFetch('/machines/rename-group',
          { method: 'PATCH', body: JSON.stringify({ field: 'area_name', db_id: dbId, old_value: oldName, new_value: trimmed, company_name: parentName }) });
      }
      // plant renames: no backend operation (db names are system-level)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMutationError(`Rename failed: ${msg}. Please refresh the page.`);
    }
  };

  const handleDeleteConfirmation = () => {
    if (!contextMenu) return;
    setDeleteConfirmation({
      type: contextMenu.type,
      id: contextMenu.id,
      name: findName(contextMenu.id),
      parentId: contextMenu.parentId,
    });
    setContextMenu(null);
  };

  const handleDeletePoint = async () => {
    if (!deleteConfirmation) return;
    const { type, id, parentId } = deleteConfirmation;

    // Optimistic local removal
    const patchMachine = (m: Machine) => ({
      ...m, points: m.points.filter(p => type !== 'point' || p.id !== id),
    });
    setTreeData(prev => {
      if (type === 'plant') return prev.filter(p => p.id !== id);
      return prev.map(plant => ({
        ...plant,
        companies: plant.companies
          .filter(c => type !== 'company' || c.id !== id)
          .map(company => ({
            ...company,
            machines: company.machines
              .filter(m => type !== 'machine' || m.id !== id)
              .map(patchMachine),
            subAreas: company.subAreas
              .filter(a => type !== 'subarea' || a.id !== id)
              .map(area => ({
                ...area,
                machines: area.machines
                  .filter(m => type !== 'machine' || m.id !== id)
                  .map(patchMachine),
              })),
          })),
      }));
    });
    setDeleteConfirmation(null);

    // Persist to backend
    try {
      if (type === 'machine') {
        await apiFetch(`/machines/${id}`, { method: 'DELETE' });
      } else if (type === 'point' && parentId) {
        await apiFetch(`/machines/${parentId}/points/${encodeURIComponent(id)}`, { method: 'DELETE' });
      }
      // plant/company/subarea deletes: local-only (not yet supported at DB level)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMutationError(`Delete failed: ${msg}. Please refresh the page.`);
      refreshTree(); // revert
    }
  };

  const handleClickOutside = () => setContextMenu(null);

  const handleAddArea = async () => {
    if (!contextMenu) return;
    const plant = treeData.find(p => p.id === contextMenu.id);
    if (!plant) { setContextMenu(null); return; }
    const dbId = dbIdFromPlantId(plant.id);
    const areaName = 'New Area';
    setContextMenu(null);
    try {
      const newMachine = await apiFetch<{id: string; name: string; db_id: number; db_name: string; company_name: string; area_name: string; area_api_id: number; active_alarms: number; label: string}>(
        '/machines/create',
        { method: 'POST', body: JSON.stringify({ name: 'New Machine', db_id: dbId, company_name: plant.name, area_name: areaName }) }
      );
      refreshTree();
      // Auto-expand so user can see and rename
      setExpandedPlants(s => new Set([...s, plant.id]));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMutationError(`Add area failed: ${msg}`);
    }
  };

  const handleAddMachine = async () => {
    if (!contextMenu) return;
    const nodeId = contextMenu.id;
    setContextMenu(null);

    // Find the context: nodeId is a company or subarea
    let dbId = 0, companyName = '', areaName = '';
    for (const plant of treeData) {
      const dbIdP = dbIdFromPlantId(plant.id);
      for (const co of plant.companies) {
        if (co.id === nodeId) {
          dbId = dbIdP; companyName = co.name; areaName = co.subAreas[0]?.name || co.name;
          break;
        }
        for (const area of co.subAreas) {
          if (area.id === nodeId) {
            dbId = dbIdP; companyName = co.name; areaName = area.name;
            break;
          }
        }
      }
    }
    if (!dbId) { setMutationError('Could not determine parent area.'); return; }

    try {
      await apiFetch('/machines/create', {
        method: 'POST',
        body: JSON.stringify({ name: 'New Machine', db_id: dbId, company_name: companyName, area_name: areaName }),
      });
      refreshTree();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMutationError(`Add machine failed: ${msg}`);
    }
  };

  const handleReportClick = (e: React.MouseEvent, machineId: string) => {
    e.stopPropagation();
    console.log('View report for machine:', machineId);
  };

  const handleAddPointButton = (e: React.MouseEvent, machine: Machine) => {
    e.stopPropagation();
    setAddPointModal({ machineId: machine.id, machineName: machine.name, points: machine.points, imageUrl: machine.imageUrl });
  };

  const updateMachineImage = (machineId: string, imageUrl: string) => {
    const patchMachine = (m: Machine) => m.id === machineId ? { ...m, imageUrl } : m;
    setTreeData(prev => prev.map(plant => ({
      ...plant,
      companies: plant.companies.map(company => ({
        ...company,
        machines: company.machines.map(patchMachine),
        subAreas: company.subAreas.map(area => ({ ...area, machines: area.machines.map(patchMachine) })),
      })),
    })));
  };

  const getAllMachines = () => {
    const result: Array<Machine & { plantName: string; areaName: string }> = [];
    treeData.forEach(plant => {
      plant.companies.forEach(company => {
        company.machines.forEach(m => result.push({ ...m, plantName: plant.name, areaName: company.name }));
        company.subAreas.forEach(area => {
          area.machines.forEach(m => result.push({ ...m, plantName: plant.name, areaName: area.name }));
        });
      });
    });
    return result;
  };

  useImperativeHandle(ref, () => ({ updateMachineImage, getAllMachines }));

  // Row style helpers — plain rows, no borders
  const rowBase  = 'w-full flex items-center gap-2 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors';  // plant
  const rowMd    = 'w-full flex items-center gap-2 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors';  // company
  const rowSm    = 'w-full flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors'; // subarea / machine
  const rowXs    = 'w-full flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors'; // point
  const rowXxs   = 'w-full flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-gray-100 transition-colors'; // axis
  const chevronBtn = 'flex-shrink-0 hover:bg-gray-200 rounded p-0.5';

  const renderMachine = (machine: Machine, areaOrCompanyId: string, companyOrPlantId: string) => (
    <div key={machine.id}>
      <div className={rowSm}>
        <button onClick={() => toggleMachine(machine.id)} className={chevronBtn}>
          {expandedMachines.has(machine.id)
            ? <ChevronDown className="w-3.5 h-3.5 text-gray-600" />
            : <ChevronRight className="w-3.5 h-3.5 text-gray-600" />}
        </button>
        <div
          onClick={() => onMachineSelect(machine.id, machine.name, machine.points, machine.imageUrl)}
          onContextMenu={(e) => handleContextMenu(e, 'machine', machine.id, areaOrCompanyId, companyOrPlantId)}
          className="flex items-center gap-2 cursor-pointer"
        >
          {editingItem?.type === 'machine' && editingItem.id === machine.id ? (
            <input type="text" defaultValue={editingItem.name} autoFocus
              onBlur={(e) => handleRenameSubmit(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(e.currentTarget.value); else if (e.key === 'Escape') setEditingItem(null); }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 px-2 py-1 border border-blue-500 rounded focus:outline-none text-xs" />
          ) : (
            <span className={`text-xs whitespace-nowrap ${getHealthTextColor(calculateMachineHealth(machine.points))}`}>{machine.name}</span>
          )}
        </div>
        {machine.hasNewReport && (
          <button onClick={(e) => handleReportClick(e, machine.id)} className="flex-shrink-0 hover:bg-blue-50 rounded p-0.5 transition-colors group">
            <FileText className="w-3.5 h-3.5 text-blue-600 group-hover:text-blue-700" />
          </button>
        )}
        <div className="relative flex-shrink-0">
          <div
            className={`w-2.5 h-2.5 rounded-full ${getHealthColor(calculateMachineHealth(machine.points))}`}
            onMouseEnter={() => setHoveredMachine(machine.id)}
            onMouseLeave={() => setHoveredMachine(null)}
          />
          {hoveredMachine === machine.id && (() => {
            const culprit = findCulpritPointAndVariable(machine.points);
            if (!culprit) return null;
            return (
              <div className="absolute right-0 top-full mt-1 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg z-50 whitespace-nowrap">
                <p className="text-xs font-semibold">{culprit.point.name}</p>
                <p className="text-xs">{culprit.metric} ({culprit.variable}): {culprit.value.toFixed(2)}</p>
              </div>
            );
          })()}
        </div>
        {isAdmin && (
          <button onClick={(e) => handleAddPointButton(e, machine)} className={chevronBtn}>
            <Plus className="w-3.5 h-3.5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Point level */}
      {expandedMachines.has(machine.id) && (
        <div className="ml-3 border-l border-gray-200 pl-1">
          {machine.points.map((point) => {
            const pointKey = `${machine.id}-${point.id}`;
            const hasAxes = (point.axes?.length ?? 0) > 0;
            return (
              <div key={point.id}>
                <div className={rowXs}>
                  {hasAxes ? (
                    <button onClick={() => togglePoint(pointKey)} className={chevronBtn}>
                      {expandedPoints.has(pointKey)
                        ? <ChevronDown className="w-3 h-3 text-gray-500" />
                        : <ChevronRight className="w-3 h-3 text-gray-500" />}
                    </button>
                  ) : (
                    <span className="w-4 flex-shrink-0" />
                  )}
                  <div
                    onClick={() => { if (onPointSelect) onPointSelect(machine.id, machine.name, machine.points, point.id, machine.imageUrl); }}
                    onContextMenu={(e) => handleContextMenu(e, 'point', point.id, machine.id, areaOrCompanyId)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    {editingItem?.type === 'point' && editingItem.id === point.id ? (
                      <input type="text" defaultValue={editingItem.name} autoFocus
                        onBlur={(e) => handleRenameSubmit(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(e.currentTarget.value); else if (e.key === 'Escape') setEditingItem(null); }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 px-2 py-1 border border-blue-500 rounded focus:outline-none text-xs" />
                    ) : (
                      <span className={`text-xs whitespace-nowrap ${getStatusTextColor(point.status)}`}>{point.name}</span>
                    )}
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(point.status)}`} />
                </div>

                {/* Axis level */}
                {expandedPoints.has(pointKey) && point.axes && (
                  <div className="ml-3 border-l border-gray-200 pl-1">
                    {point.axes.map((ax) => (
                      <div
                        key={ax.axis_num}
                        className={rowXxs}
                        onClick={() => { if (onPointSelect) onPointSelect(machine.id, machine.name, machine.points, point.id, machine.imageUrl, ax.axis_num); }}
                      >
                        <span className="w-4 flex-shrink-0" />
                        <span className={`text-xs flex-1 ${ax.in_alarm ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {axisLabel(ax.axis_num)}
                        </span>
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ax.in_alarm ? 'bg-red-500' : 'bg-green-500'}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const displayedPlants = selectedDbId
    ? treeData.filter(p => p.id === selectedDbId)
    : treeData;

  return (
    <div ref={containerRef} onClick={handleClickOutside}>
      <div>
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Machine Hierarchy</h2>

        {/* Database selector */}
        <div className="mb-3">
          <select
            value={selectedDbId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedDbId(val);
              if (!val) {
                onFilterSelect?.(null);
              } else {
                const plant = treeData.find(p => p.id === val);
                if (plant) onFilterSelect?.({ dbName: plant.name, label: plant.name });
              }
            }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
          >
            <option value="">All Databases</option>
            {treeData.map(plant => (
              <option key={plant.id} value={plant.id}>{plant.name}</option>
            ))}
          </select>
        </div>

        {treeLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}
          </div>
        )}
        {treeError && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{treeError}</div>
        )}

        <div>
          {displayedPlants.map((plant) => (
            <div key={plant.id}>
              {/* Plant level — text-sm */}
              <div className={rowBase}>
                <button onClick={() => togglePlant(plant.id)} className={chevronBtn}>
                  {expandedPlants.has(plant.id)
                    ? <ChevronDown className="w-4 h-4 text-gray-600" />
                    : <ChevronRight className="w-4 h-4 text-gray-600" />}
                </button>
                <div
                  onClick={() => {
                    togglePlant(plant.id);
                    onFilterSelect?.({ dbName: plant.name, label: plant.name });
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'plant', plant.id)}
                  className="flex-1 cursor-pointer min-w-0"
                >
                  {editingItem?.type === 'plant' && editingItem.id === plant.id ? (
                    <input type="text" defaultValue={editingItem.name} autoFocus
                      onBlur={(e) => handleRenameSubmit(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(e.currentTarget.value); else if (e.key === 'Escape') setEditingItem(null); }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none text-sm" />
                  ) : (
                    <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{plant.name}</span>
                  )}
                </div>
              </div>

              {/* Company level — text-sm, ml-4 */}
              {expandedPlants.has(plant.id) && (
                <div className="ml-3 border-l border-gray-200 pl-1">
                  {plant.companies.map((company) => (
                    <div key={company.id}>
                      <div className={rowMd}>
                        <button onClick={() => toggleCompany(company.id)} className={chevronBtn}>
                          {expandedCompanies.has(company.id)
                            ? <ChevronDown className="w-4 h-4 text-gray-600" />
                            : <ChevronRight className="w-4 h-4 text-gray-600" />}
                        </button>
                        <div
                          onClick={() => {
                            onFilterSelect?.({
                              dbName: plant.name,
                              companyName: company.isAreaLevel ? undefined : company.name,
                              areaName: company.isAreaLevel ? company.name : undefined,
                              label: `${plant.name} > ${company.name}`,
                            });
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'company', company.id, plant.id)}
                          className="flex-1 cursor-pointer min-w-0"
                        >
                          {editingItem?.type === 'company' && editingItem.id === company.id ? (
                            <input type="text" defaultValue={editingItem.name} autoFocus
                              onBlur={(e) => handleRenameSubmit(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(e.currentTarget.value); else if (e.key === 'Escape') setEditingItem(null); }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none text-sm" />
                          ) : (
                            <span className="text-sm text-gray-800 whitespace-nowrap">{company.name}</span>
                          )}
                        </div>
                      </div>

                      {expandedCompanies.has(company.id) && (
                        <div className="ml-3 border-l border-gray-200 pl-1">
                          {company.subAreas.length > 0 ? (
                            // Berry-style: SubArea level — text-xs
                            company.subAreas.map((area) => (
                              <div key={area.id}>
                                <div className={rowSm}>
                                  <button onClick={() => toggleSubArea(area.id)} className={chevronBtn}>
                                    {expandedSubAreas.has(area.id)
                                      ? <ChevronDown className="w-3.5 h-3.5 text-gray-600" />
                                      : <ChevronRight className="w-3.5 h-3.5 text-gray-600" />}
                                  </button>
                                  <div
                                    onClick={() => {
                                      onFilterSelect?.({
                                        dbName: plant.name,
                                        companyName: company.name,
                                        areaName: area.name,
                                        label: `${plant.name} > ${company.name} > ${area.name}`,
                                      });
                                    }}
                                    onContextMenu={(e) => handleContextMenu(e, 'subarea', area.id, company.id, plant.id)}
                                    className="flex-1 cursor-pointer min-w-0"
                                  >
                                    {editingItem?.type === 'subarea' && editingItem.id === area.id ? (
                                      <input type="text" defaultValue={editingItem.name} autoFocus
                                        onBlur={(e) => handleRenameSubmit(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(e.currentTarget.value); else if (e.key === 'Escape') setEditingItem(null); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none text-xs" />
                                    ) : (
                                      <span className="text-xs text-gray-700 whitespace-nowrap">{area.name}</span>
                                    )}
                                  </div>
                                </div>
                                {expandedSubAreas.has(area.id) && (
                                  <div className="ml-3 border-l border-gray-200 pl-1">
                                    {area.machines.map(m => renderMachine(m, area.id, company.id))}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            // Arcadium-style: machines directly under company
                            company.machines.map(m => renderMachine(m, company.id, plant.id))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Context Menu — admin only */}
      {isAdmin && contextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-40"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={handleRename} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors text-gray-700">Rename</button>
          <button onClick={handleDeleteConfirmation} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors text-red-600">Delete</button>
          <div className="border-t border-gray-200 my-1" />
          {contextMenu.type === 'plant' && (
            <button onClick={handleAddArea} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors text-gray-700">Add Area</button>
          )}
          {(contextMenu.type === 'company' || contextMenu.type === 'subarea') && (
            <button onClick={handleAddMachine} className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors text-gray-700">Add Machine</button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmation && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-30 z-50" onClick={() => setDeleteConfirmation(null)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 min-w-96" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4">
              <h3 className="text-gray-900 mb-2">Confirm Deletion</h3>
              <p className="text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{deleteConfirmation.type} &quot;{deleteConfirmation.name}&quot;</span>?
                {deleteConfirmation.type !== 'point' && <span className="block mt-2 text-red-600">This will also delete all nested items.</span>}
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
              <button onClick={handleDeletePoint} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">Delete</button>
              <button onClick={() => setDeleteConfirmation(null)} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* Add Point Modal */}
      {addPointModal && (
        <AddPointModal
          machineName={addPointModal.machineName}
          onClose={() => setAddPointModal(null)}
          onAddPoint={async (pointName) => {
            const machineId = addPointModal.machineId;
            setAddPointModal(null);
            try {
              await apiFetch(`/machines/${machineId}/points`, {
                method: 'POST',
                body: JSON.stringify({ point_name: pointName, axes: [1, 2, 3] }),
              });
              // Refresh the expanded machine detail so the new point shows
              setExpandedMachines(prev => {
                const next = new Set(prev);
                next.delete(machineId); // collapse to force re-fetch on re-expand
                return next;
              });
              refreshTree();
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : String(err);
              setMutationError(`Add point failed: ${msg}`);
            }
          }}
        />
      )}

      {/* Mutation error toast */}
      {mutationError && (
        <div className="fixed bottom-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between z-50 shadow-lg">
          <span className="text-sm text-red-700">{mutationError}</span>
          <button onClick={() => setMutationError(null)} className="text-red-500 hover:text-red-700 ml-3 text-lg leading-none">×</button>
        </div>
      )}
    </div>
  );
});
